// frontend/src/store/uiStore.js
// Store de Zustand para gestionar el estado de la UI, como carga y mensajes.

import { create } from 'zustand';

/**
 * @typedef {object} UIState
 * @property {boolean} isLoading - Indica si hay una operación de carga en progreso.
 * @property {boolean} showMessage - Indica si un mensaje está visible.
 * @property {string} messageText - El texto del mensaje a mostrar.
 * @property {'success'|'error'|'info'} messageType - El tipo de mensaje para estilizado.
 * @property {function(boolean)} setIsLoading - Establece el estado de carga.
 * @property {function(string, 'success'|'error'|'info', number)} showTemporaryMessage - Muestra un mensaje temporal.
 * @property {function()} hideMessage - Oculta el mensaje.
 */

/**
 * @type {import('zustand').UseBoundStore<UIState>}
 */
export const useUIStore = create((set) => ({
  isLoading: false,
  showMessage: false,
  messageText: '',
  messageType: 'info', // 'success', 'error', 'info'
  messageTimeoutId: null, // Para manejar el timeout del mensaje

  /**
   * @function setIsLoading
   * @description Establece el estado de carga global.
   * @param {boolean} loading - True para mostrar carga, false para ocultar.
   */
  setIsLoading: (loading) => set({ isLoading: loading }),

  /**
   * @function showTemporaryMessage
   * @description Muestra un mensaje temporal en la UI.
   * @param {string} text - El texto del mensaje.
   * @param {'success'|'error'|'info'} type - El tipo de mensaje.
   * @param {number} duration - Duración en milisegundos antes de ocultar el mensaje.
   */
  showTemporaryMessage: (text, type = 'info', duration = 3000) => {
    set((state) => {
      // Limpia cualquier timeout anterior para evitar solapamientos
      if (state.messageTimeoutId) {
        clearTimeout(state.messageTimeoutId);
      }
      const timeoutId = setTimeout(() => {
        set({ showMessage: false, messageText: '' });
      }, duration);
      return {
        showMessage: true,
        messageText: text,
        messageType: type,
        messageTimeoutId: timeoutId,
      };
    });
  },

  /**
   * @function hideMessage
   * @description Oculta cualquier mensaje visible.
   */
  hideMessage: () => {
    set((state) => {
      if (state.messageTimeoutId) {
        clearTimeout(state.messageTimeoutId);
      }
      return { showMessage: false, messageText: '', messageTimeoutId: null };
    });
  },
}));
